import { defineComponent as _defineComponent } from 'vue';
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString } from "vue";
import { computed, ref } from "vue";
import usersService from "@/services/usersService";
import { tryParseApiErrors } from "@/utils";
import { FORM_VALIDATION_PATTERNS } from "@/utils/constants";
export default /*@__PURE__*/ _defineComponent({
    __name: 'InviteProviderDialog',
    props: { modelValue: { type: Boolean, default: false } },
    emits: ["update:modelValue"],
    setup(__props, { emit: __emit }) {
        const emit = __emit;
        const ROLE_LABELS = { prescriber: "Provider", office_staff: "Office staff" };
        const STATUS_LABELS = { sent: "Invite sent", resent: "Invite re-sent", active: "Already has access" };
        const step = ref("npi");
        const npi = ref("");
        const firstName = ref("");
        const lastName = ref("");
        const fax = ref("");
        const email = ref("");
        const officeEmails = ref([]);
        const registryFound = ref(false);
        const registryLine = ref("");
        const alreadyRegistered = ref(false);
        const alreadyActive = ref(false);
        const deactivated = ref(false);
        const sentInvites = ref([]);
        const loading = ref(false);
        const submitClicked = ref(false);
        const errors = ref([]);
        const npiDigits = computed(() => npi.value.replace(/[^0-9]/g, ""));
        const npiError = computed(() => (npi.value && npiDigits.value.length !== 10 ? "An NPI is exactly 10 digits" : ""));
        const registrySummary = computed(() => registryLine.value);
        const showPrescriberFields = computed(() => !alreadyRegistered.value && registryFound.value);
        const canSend = computed(() => !deactivated.value && (alreadyRegistered.value || registryFound.value));
        const anyInviteSent = computed(() => sentInvites.value.some((invite) => invite.status !== "active"));
        const cleanOfficeEmails = computed(() => officeEmails.value.map((value) => value.trim()).filter(Boolean));
        const invalidOfficeEmails = computed(() => cleanOfficeEmails.value.filter((value) => !FORM_VALIDATION_PATTERNS.EMAIL_PATTERN.test(value)));
        const officeEmailsError = computed(() => {
            if (invalidOfficeEmails.value.length)
                return `Not a valid email: ${invalidOfficeEmails.value.join(", ")}`;
            if (submitClicked.value && alreadyActive.value && !cleanOfficeEmails.value.length) {
                return "Add at least one office staff email";
            }
            return "";
        });
        async function lookup() {
            if (npiDigits.value.length !== 10) {
                errors.value = ["Enter a valid 10-digit NPI."];
                return;
            }
            loading.value = true;
            errors.value = [];
            try {
                const info = await usersService.lookupProviderInvite(npiDigits.value);
                registryFound.value = Boolean(info.found);
                alreadyRegistered.value = Boolean(info.alreadyRegistered);
                alreadyActive.value = Boolean(info.alreadyActive);
                deactivated.value = Boolean(info.deactivated);
                firstName.value = info.firstName || "";
                lastName.value = info.lastName || "";
                fax.value = info.fax || "";
                registryLine.value = [
                    [info.firstName, info.lastName].filter(Boolean).join(" ") + (info.credential ? `, ${info.credential}` : ""),
                    info.specialty,
                    [info.city, info.state].filter(Boolean).join(", "),
                ]
                    .filter(Boolean)
                    .join(" — ");
                step.value = "confirm";
            }
            catch (error) {
                errors.value = tryParseApiErrors(error);
            }
            finally {
                loading.value = false;
            }
        }
        async function send() {
            submitClicked.value = true;
            if (officeEmailsError.value)
                return;
            if (showPrescriberFields.value && (!firstName.value.trim() || !lastName.value.trim() || !email.value.trim()))
                return;
            loading.value = true;
            errors.value = [];
            try {
                const result = await usersService.inviteProvider({
                    npi: npiDigits.value,
                    ...(showPrescriberFields.value
                        ? {
                            email: email.value.trim(),
                            firstName: firstName.value.trim(),
                            lastName: lastName.value.trim(),
                            fax: fax.value.trim() || null,
                        }
                        : {}),
                    officeEmails: cleanOfficeEmails.value,
                });
                sentInvites.value = result.invites;
                step.value = "sent";
            }
            catch (error) {
                errors.value = tryParseApiErrors(error);
            }
            finally {
                loading.value = false;
            }
        }
        function reset() {
            step.value = "npi";
            registryFound.value = false;
            alreadyRegistered.value = false;
            alreadyActive.value = false;
            deactivated.value = false;
            submitClicked.value = false;
            errors.value = [];
            // Clear the identity fields too: "Different NPI" must never carry one provider's
            // email or names into another provider's invite.
            npi.value = "";
            firstName.value = "";
            lastName.value = "";
            fax.value = "";
            email.value = "";
            officeEmails.value = [];
            sentInvites.value = [];
        }
        function close() {
            emit("update:modelValue", false);
            reset();
        }
        return (_ctx, _cache) => {
            const _component_v_card_title = _resolveComponent("v-card-title");
            const _component_v_alert = _resolveComponent("v-alert");
            const _component_v_list_item = _resolveComponent("v-list-item");
            const _component_v_list = _resolveComponent("v-list");
            const _component_v_btn = _resolveComponent("v-btn");
            const _component_v_text_field = _resolveComponent("v-text-field");
            const _component_v_combobox = _resolveComponent("v-combobox");
            const _component_v_card_text = _resolveComponent("v-card-text");
            const _component_v_card = _resolveComponent("v-card");
            const _component_v_dialog = _resolveComponent("v-dialog");
            return (_openBlock(), _createBlock(_component_v_dialog, {
                "model-value": __props.modelValue,
                "max-width": "480",
                scrollable: "",
                "onUpdate:modelValue": close
            }, {
                default: _withCtx(() => [
                    _createVNode(_component_v_card, null, {
                        default: _withCtx(() => [
                            _createVNode(_component_v_card_title, {
                                tag: "h2",
                                class: "text-h6 px-6 pt-6 pb-1"
                            }, {
                                default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                                        _createTextVNode("Invite a provider", -1)
                                    ]))]),
                                _: 1
                            }),
                            _createVNode(_component_v_card_text, { class: "px-6 pt-0 pb-6" }, {
                                default: _withCtx(() => [
                                    (step.value === 'sent')
                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                            (anyInviteSent.value)
                                                ? (_openBlock(), _createBlock(_component_v_alert, {
                                                    key: 0,
                                                    type: "success",
                                                    variant: "tonal",
                                                    density: "compact",
                                                    class: "mt-3 mb-2"
                                                }, {
                                                    default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                                                            _createTextVNode(" Each invited person sets a password from their email to sign in. ", -1)
                                                        ]))]),
                                                    _: 1
                                                }))
                                                : (_openBlock(), _createBlock(_component_v_alert, {
                                                    key: 1,
                                                    type: "info",
                                                    variant: "tonal",
                                                    density: "compact",
                                                    class: "mt-3 mb-2"
                                                }, {
                                                    default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                                                            _createTextVNode(" Everyone listed already has access. No invites were sent. ", -1)
                                                        ]))]),
                                                    _: 1
                                                })),
                                            _createVNode(_component_v_list, {
                                                density: "compact",
                                                class: "mb-4"
                                            }, {
                                                default: _withCtx(() => [
                                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sentInvites.value, (invite) => {
                                                        return (_openBlock(), _createBlock(_component_v_list_item, {
                                                            key: invite.email,
                                                            title: invite.email,
                                                            subtitle: `${ROLE_LABELS[invite.role]} · ${STATUS_LABELS[invite.status]}`,
                                                            class: "px-0"
                                                        }, null, 8, ["title", "subtitle"]));
                                                    }), 128))
                                                ]),
                                                _: 1
                                            }),
                                            _createVNode(_component_v_btn, {
                                                color: "primary",
                                                block: "",
                                                onClick: close
                                            }, {
                                                default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                                                        _createTextVNode("Done", -1)
                                                    ]))]),
                                                _: 1
                                            })
                                        ], 64))
                                        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                            _cache[15] || (_cache[15] = _createElementVNode("p", { class: "text-body-2 text-medium-emphasis mb-4" }, " The provider and their office staff get read-only access to the prior auths filed under the provider's NPI. ", -1)),
                                            _createVNode(_component_v_text_field, {
                                                modelValue: npi.value,
                                                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((npi).value = $event)),
                                                label: "Provider NPI",
                                                placeholder: "10 digits",
                                                disabled: step.value === 'confirm',
                                                "error-messages": npiError.value,
                                                class: "mb-2 lh--force-white-background"
                                            }, null, 8, ["modelValue", "disabled", "error-messages"]),
                                            (step.value === 'npi')
                                                ? (_openBlock(), _createBlock(_component_v_btn, {
                                                    key: 0,
                                                    color: "primary",
                                                    block: "",
                                                    loading: loading.value,
                                                    onClick: lookup
                                                }, {
                                                    default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                                                            _createTextVNode("Look up NPI", -1)
                                                        ]))]),
                                                    _: 1
                                                }, 8, ["loading"]))
                                                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                    (deactivated.value)
                                                        ? (_openBlock(), _createBlock(_component_v_alert, {
                                                            key: 0,
                                                            type: "warning",
                                                            variant: "tonal",
                                                            density: "compact",
                                                            class: "mb-4"
                                                        }, {
                                                            default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                                                                    _createTextVNode(" This provider's access was deactivated. Contact Lamar Health to restore it. ", -1)
                                                                ]))]),
                                                            _: 1
                                                        }))
                                                        : (alreadyRegistered.value)
                                                            ? (_openBlock(), _createBlock(_component_v_alert, {
                                                                key: 1,
                                                                type: "info",
                                                                variant: "tonal",
                                                                density: "compact",
                                                                class: "mb-4"
                                                            }, {
                                                                default: _withCtx(() => [
                                                                    _createTextVNode(_toDisplayString(alreadyActive.value
                                                                        ? "This provider already has a login. Add office staff below."
                                                                        : "This provider has a pending invite. Sending re-sends it."), 1)
                                                                ]),
                                                                _: 1
                                                            }))
                                                            : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                                                (registryFound.value)
                                                                    ? (_openBlock(), _createBlock(_component_v_alert, {
                                                                        key: 0,
                                                                        type: "success",
                                                                        variant: "tonal",
                                                                        density: "compact",
                                                                        class: "mb-4"
                                                                    }, {
                                                                        default: _withCtx(() => [
                                                                            _cache[12] || (_cache[12] = _createTextVNode(" NPI registry match: ", -1)),
                                                                            _createElementVNode("strong", null, _toDisplayString(registrySummary.value), 1)
                                                                        ]),
                                                                        _: 1
                                                                    }))
                                                                    : (_openBlock(), _createBlock(_component_v_alert, {
                                                                        key: 1,
                                                                        type: "warning",
                                                                        variant: "tonal",
                                                                        density: "compact",
                                                                        class: "mb-4"
                                                                    }, {
                                                                        default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                                                                                _createTextVNode(" This NPI could not be verified in the NPI registry. Check the number, or try again later. ", -1)
                                                                            ]))]),
                                                                        _: 1
                                                                    }))
                                                            ], 64)),
                                                    (showPrescriberFields.value)
                                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                                                            _createVNode(_component_v_text_field, {
                                                                modelValue: firstName.value,
                                                                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((firstName).value = $event)),
                                                                label: "First Name",
                                                                "error-messages": submitClicked.value && !firstName.value.trim() ? 'Required' : '',
                                                                class: "mb-2 lh--force-white-background"
                                                            }, null, 8, ["modelValue", "error-messages"]),
                                                            _createVNode(_component_v_text_field, {
                                                                modelValue: lastName.value,
                                                                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((lastName).value = $event)),
                                                                label: "Last Name",
                                                                "error-messages": submitClicked.value && !lastName.value.trim() ? 'Required' : '',
                                                                class: "mb-2 lh--force-white-background"
                                                            }, null, 8, ["modelValue", "error-messages"]),
                                                            _createVNode(_component_v_text_field, {
                                                                modelValue: fax.value,
                                                                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((fax).value = $event)),
                                                                label: "Provider Fax",
                                                                hint: "From the NPI registry — edit if needed.",
                                                                "persistent-hint": "",
                                                                class: "mb-2 lh--force-white-background"
                                                            }, null, 8, ["modelValue"]),
                                                            _createVNode(_component_v_text_field, {
                                                                modelValue: email.value,
                                                                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((email).value = $event)),
                                                                label: "Provider Email",
                                                                type: "email",
                                                                "error-messages": submitClicked.value && !email.value.trim() ? 'Required' : '',
                                                                class: "mb-2 lh--force-white-background"
                                                            }, null, 8, ["modelValue", "error-messages"])
                                                        ], 64))
                                                        : _createCommentVNode("", true),
                                                    (canSend.value)
                                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 4 }, [
                                                            _createVNode(_component_v_combobox, {
                                                                modelValue: officeEmails.value,
                                                                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => ((officeEmails).value = $event)),
                                                                label: alreadyActive.value ? 'Office staff emails' : 'Office staff emails (optional)',
                                                                hint: "Each person gets their own read-only login. Press Enter after each email.",
                                                                "persistent-hint": "",
                                                                multiple: "",
                                                                chips: "",
                                                                "closable-chips": "",
                                                                delimiters: [',', ';', ' '],
                                                                "error-messages": officeEmailsError.value,
                                                                class: "mb-4 lh--force-white-background"
                                                            }, null, 8, ["modelValue", "label", "error-messages"]),
                                                            _createVNode(_component_v_btn, {
                                                                color: "primary",
                                                                block: "",
                                                                loading: loading.value,
                                                                class: "mb-2",
                                                                onClick: send
                                                            }, {
                                                                default: _withCtx(() => [
                                                                    _createTextVNode(_toDisplayString(cleanOfficeEmails.value.length ? "Send invites" : "Send invite"), 1)
                                                                ]),
                                                                _: 1
                                                            }, 8, ["loading"])
                                                        ], 64))
                                                        : _createCommentVNode("", true),
                                                    _createVNode(_component_v_btn, {
                                                        variant: "text",
                                                        block: "",
                                                        onClick: reset
                                                    }, {
                                                        default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                                                                _createTextVNode("Different NPI", -1)
                                                            ]))]),
                                                        _: 1
                                                    })
                                                ], 64)),
                                            (errors.value.length)
                                                ? (_openBlock(), _createBlock(_component_v_alert, {
                                                    key: 2,
                                                    type: "error",
                                                    variant: "tonal",
                                                    class: "mt-4"
                                                }, {
                                                    default: _withCtx(() => [
                                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errors.value, (err) => {
                                                            return (_openBlock(), _createElementBlock("div", { key: err }, _toDisplayString(err), 1));
                                                        }), 128))
                                                    ]),
                                                    _: 1
                                                }))
                                                : _createCommentVNode("", true)
                                        ], 64))
                                ]),
                                _: 1
                            })
                        ]),
                        _: 1
                    })
                ]),
                _: 1
            }, 8, ["model-value"]));
        };
    }
});
